import React, { lazy, Suspense } from "react";
import { SEOHead } from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import { LazySection } from "@/components/LazySection";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  MapPin,
  ArrowRight,
  Phone,
  Star,
  ShieldCheck,
  TrendingUp,
  Users,
} from "lucide-react";

const Footer = lazy(() => import("@/components/Footer"));

const credentials = [
  {
    icon: ShieldCheck,
    label: "Active STR Investor",
    desc: "Personally owns and operates short-term rentals in the same Northern Virginia markets we serve.",
  },
  {
    icon: Star,
    label: "Airbnb Superhost",
    desc: "Earned Superhost status by consistently delivering 5-star guest experiences.",
  },
  {
    icon: TrendingUp,
    label: "Active STR Investor",
    desc: "Personally owns and operates short-term rentals in Northern Virginia markets.",
  },
  {
    icon: Users,
    label: "Airbnb Expert",
    desc: "Deep platform expertise — listings, SEO, algorithm optimization, and pricing tools.",
  },
];

const markets = [
  {
    county: "Fairfax County",
    note: "60-night/year cap. Owner-occupancy required. We handle permit applications and annual renewals.",
  },
  {
    county: "Alexandria City",
    note: "No nightly cap. Investor-friendly. Full permit process management including safety compliance.",
  },
  {
    county: "Arlington County",
    note: "Owner-occupied primary residence only. Strong demand from Pentagon and hospital corridors.",
  },
  {
    county: "Loudoun County",
    note: "STR-friendly jurisdiction with a straightforward permit process. Great for investor properties.",
  },
  {
    county: "Prince William County",
    note: "No nightly cap or residency requirements in unincorporated areas. Owner and investor eligible.",
  },
];

const AboutPage = () => {
  const scrollToForm = () => {
    window.location.href = "/#lead-form";
  };

  return (
    <div className="min-h-screen bg-background font-body">
      <SEOHead
        title="About Jason Nguyen | JNN Homes Founder & STR Investor"
        description="Learn about Jason Nguyen, an active real estate investor and Airbnb Superhost leading JNN Homes in Northern Virginia."
        canonical="https://www.jnnhomes.com/about"
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "About JNN Homes",
          url: "https://www.jnnhomes.com/about",
          mainEntity: {
            "@type": "Organization",
            name: "JNN Homes",
            founder: {
              "@type": "Person",
              name: "Jason Nguyen",
              jobTitle: "Founder & STR Investor",
              knowsAbout: [
                "Short-term rental management",
                "Airbnb hosting",
                "Real estate investing",
                "Northern Virginia rental market",
              ],
            },
          },
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 bg-gradient-to-br from-background via-background to-muted/50 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] opacity-20 pointer-events-none bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/40 via-transparent to-transparent" />
        <div className="container relative mx-auto px-4 text-center max-w-4xl">
          <p className="text-primary font-semibold uppercase tracking-widest text-sm mb-4">
            About JNN Homes
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground leading-tight mb-6">
            Built by an Investor.
            <br />
            <span className="text-primary italic">For Investors.</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            JNN Homes isn't a generic property management company. We're a team
            led by an active STR investor who personally operates STRs — so we
            know exactly what it takes to make your property perform.
          </p>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-24 bg-background" id="about">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto rounded-3xl border border-primary/20 bg-card/40 p-8 md:p-12 shadow-[0_0_40px_-15px_rgba(201,168,76,0.15)]">
            <div className="flex flex-col md:flex-row gap-12 items-center">
              <div className="w-full md:w-2/5 flex justify-center">
                <div className="relative w-64 h-64 md:w-80 md:h-80 rounded-full overflow-hidden border-4 border-primary/20 shadow-2xl shrink-0">
                  <img
                    src="https://vibe.filesafe.space/1778679421893490393/attachments/6e9bdba2-4eaa-4a8d-9bc3-956a66e91cda.jpg"
                    alt="Jason Nguyen — JNN Homes Founder, STR Investor and Airbnb Superhost"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
                </div>
              </div>

              <div className="w-full md:w-3/5 space-y-6 text-center md:text-left">
                <div>
                  <h2 className="text-4xl md:text-5xl font-heading font-bold text-foreground leading-tight">
                    Meet
                    <br />
                    <span className="text-primary">Jason Nguyen</span>
                  </h2>
                  <div className="flex flex-wrap gap-2 justify-center md:justify-start mt-4">
                    <Badge
                      variant="outline"
                      className="border-primary/40 text-primary bg-primary/5 text-sm py-1"
                    >
                      Superhost
                    </Badge>
                    <Badge
                      variant="outline"
                      className="border-primary/40 text-primary bg-primary/5 text-sm py-1"
                    >
                      Investor
                    </Badge>
                    <Badge
                      variant="outline"
                      className="border-primary/40 text-primary bg-primary/5 text-sm py-1"
                    >
                      Airbnb Expert
                    </Badge>
                  </div>
                </div>
                <div className="space-y-4 text-muted-foreground text-lg leading-relaxed">
                  <p>
                    As a hands-on Airbnb host and local expert, Jason
                    understands exactly what it takes to make a home shine on
                    Airbnb. He is deeply knowledgeable about the local Airbnb
                    markets across{" "}
                    <strong className="text-foreground">
                      Washington, DC, Alexandria, Arlington, Fairfax, Loudoun,
                      and Prince William
                    </strong>{" "}
                    counties.
                  </p>
                  <p>
                    His mission is simple: help homeowners earn more from their
                    homes, hands-off, while every guest gets a 5-star stay.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 pt-2 justify-center md:justify-start">
                  <Button
                    onClick={scrollToForm}
                    className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold px-6 py-5 h-auto rounded-full"
                  >
                    Get a Free Consultation
                  </Button>
                  <a href="tel:7038905425">
                    <Button
                      variant="outline"
                      className="font-bold px-6 py-5 h-auto rounded-full w-full sm:w-auto"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      (703) 890-5425
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="py-20 bg-muted/20 border-y border-border">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 space-y-3">
            <h2 className="text-3xl font-heading font-bold">
              Why Jason is Different
            </h2>
            <p className="text-muted-foreground">
              Most co-hosts are hobbyists. Jason is an active investor who
              operates in the same market as your property.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {credentials.map((cred, i) => {
              const Icon = cred.icon;
              return (
                <div
                  key={i}
                  className="bg-card border border-border rounded-2xl p-6 flex flex-col gap-4 hover:border-primary/30 transition-colors"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                    <Icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground mb-1">
                      {cred.label}
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {cred.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Market Expertise */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 space-y-3">
            <h2 className="text-3xl font-heading font-bold">
              Deep Local Market Knowledge
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Every VA & DC county has different STR rules, demand patterns, and
              permit requirements. We know them all cold.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {markets.map((m, i) => (
              <div
                key={i}
                className="flex gap-4 bg-card border border-border rounded-xl p-5 hover:border-primary/30 transition-colors"
              >
                <MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-bold text-foreground mb-1">{m.county}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {m.note}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission / Values */}
      <section className="py-20 bg-muted/20 border-y border-border">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-heading font-bold mb-4">
              Our Operating Principles
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              "We only take on properties where we see genuine Airbnb potential — no false promises.",
              "We manage our own STRs in the same markets, so our advice is grounded in real experience.",
              "Transparency is non-negotiable: every owner gets monthly reports with full revenue and expense detail.",
              "We treat every property like it's our own investment — because in a sense, our reputation depends on it.",
              "No lock-in contracts. If we're not performing, you should be free to leave.",
              "We stay ahead of every county's evolving STR regulations so your permit is always clean.",
            ].map((val, i) => (
              <div
                key={i}
                className="flex items-start gap-3 bg-card border border-border rounded-xl p-5"
              >
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {val}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary/10 border-y border-primary/20">
        <div className="container mx-auto px-4 text-center max-w-2xl">
          <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
            Work With Someone Who Knows VA & DC STRs
          </h2>
          <p className="text-muted-foreground mb-8">
            Let Jason and the JNN Homes team run a free revenue analysis on your
            property — no commitment required.
          </p>
          <Button
            onClick={scrollToForm}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-lg px-8 py-6 h-auto rounded-full w-full max-w-sm"
          >
            Get a Free Revenue Analysis
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <LazySection
        minHeight="160px"
        rootMargin="200px"
        fallback={<div className="h-40 bg-card/20" />}
      >
        <Suspense fallback={<div className="h-40 bg-card/20" />}>
          <Footer />
        </Suspense>
      </LazySection>
    </div>
  );
};

export default AboutPage;
